/**
 * Validates if the input field has a value.
 * @param {Object} value - Object containing the input value.
 * @returns {Array} - Array containing a boolean indicating if the input has a value, and an error message if it doesn't.
 */
export const validateHasValue = (value) => {
    let hasValue;
    let error;

    if (value.value) {
        hasValue = true;
        error = null;
    } else {
        hasValue = false;
        error = "Debes ingresar un valor"
    }

    return [hasValue, error]
}

/**
 * Validates if the input is a valid phone number.
 * @param {Object} number - Object containing the input phone number value.
 * @returns {Array} - Array containing a boolean indicating if the input is a valid phone number, and an error message if it isn't.
 */
export const validatePhoneNumber = (number) => {
    let isValid;
    let error;

    const phoneNumberRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;

    // console.log(phoneNumberRegex.test(number.value))

    if (phoneNumberRegex.test(number.value)) {
        isValid = true;
        error = null;
    } else {
        isValid = false;
        error = "Debes ingresar un teléfono válido."
    }

    return [isValid, error]
}

/**
 * Validates if the input is a valid email address.
 * @param {Object} email - Object containing the input email value.
 * @returns {Array} - Array containing a boolean indicating if the input is a valid email address, and an error message if it isn't.
 */
export const validateEmail = (email) => {
    let isValid;
    let error;

    const emailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;

    if (emailRegex.test(email.value)) {
        isValid = true;
        error = null;
    } else {
        isValid = false;
        error = "Debes ingresar un email válido."
    }

    return [isValid, error]
}

/**
 * Checks if the given RUT (Chilean national identification number) is valid.
 * @param {Object} rut - Object containing the input RUT value.
 * @returns {boolean} - Returns true if the input RUT is valid, false otherwise.
 */
const isRutValid = (rut) => {
    if (!rut.value) return false;

    let sum = 0;
    const rut_clean = String(rut.value)
        .toLowerCase()
        .match(/[\dk]+/g)
        .join("");
    const rut_body = rut_clean.slice(0, -1);
    const rut_vd = rut_clean.slice(-1);

    const multipliers = [2, 3, 4, 5, 6, 7];

    if (rut_body.length < 7) return false;

    for (let i = 0; i < rut_body.length; i++) {
        const num = rut_body[rut_body.length - 1 - i];
        const by =
            i < multipliers.length
                ? multipliers[i]
                : multipliers[i % multipliers.length];
        const subtotal = num * by;
        sum += num * by;
    }

    const vd = 11 - (sum % 11);
    if (vd === 10 && rut_vd === "k") return true;
    if (vd === 11 && rut_vd === "0") return true;
    if (String(vd) === rut_vd) return true;

    return false;
}

/**
 * Validates if the input is a valid RUT (Chilean national identification number).
 * @param {Object} rut - Object containing the input RUT value.
 * @returns {Array} - Array containing a boolean indicating if the input is a valid RUT, and an error message if it isn't.
 */
export const validateRut = (rut) => {
    let isValid;
    let error;

    if (isRutValid(rut)) {
        isValid = true;
        error = null;
    } else {
        isValid = false;
        error = "Debes ingresar un RUT válido."
    }

    return [isValid, error]
}

/**
 * Validates if the input is a valid VIN (Vehicle Identification Number) or license plate.
 * @param {Object} vin - Object containing the input VIN or license plate value.
 * @returns {Array} - Array containing a boolean indicating if the input is a valid VIN or license plate, and an error message if it isn't.
 */
export const vinIsValid = (vin) => {
    let isValid;
    let error;

    const vinRegex = /^(?=.*[0-9])(?=.*[A-z])[0-9A-z-]{17}$/;
    const licensePlateRegex = /^(?:[A-Za-z]{4}\d{2}|\d{2}[A-Za-z]{4})$/;

    // console.log(licensePlateRegex.test(vin.value))

    if (vinRegex.test(vin.value) || licensePlateRegex.test(vin.value)) {
        isValid = true;
        error = false;
    } else {
        isValid = false;
        error = "Debes ingresar un VIN o una patente válida."
    }

    return [isValid, error]
}

/**
 * Validate personal data form by checking the validity of name, phone, email, and RUT fields.
 * Updates the state for each field with the corresponding validation results.
 * Toggles the personalDataOpen state if all fields are valid.
 */
export const validatePersonalData = () => {
    // console.log("validatePersonalData runs")

    const [isNameValid, nameError] = validateHasValue(name);
    const [isPhoneValid, phoneError] = validatePhoneNumber(phone);
    const [isEmailValid, emailError] = validateEmail(email);
    const [isRutValid, rutError] = validateRut(rut);

    setName((prevState) => {
        return {
            ...prevState,
            isValid: isNameValid,
            error: nameError
        }
    })

    setPhone((prevState) => {
        return {
            ...prevState,
            isValid: isPhoneValid,
            error: phoneError
        }
    })

    setEmail((prevState) => {
        return {
            ...prevState,
            isValid: isEmailValid,
            error: emailError
        }
    })

    setRut((prevState) => {
        return {
            ...prevState,
            isValid: isRutValid,
            error: rutError
        }
    })

    if (isNameValid && isPhoneValid && isEmailValid && isRutValid) {
        setPersonalDataOpen(!personalDataOpen);
    }
}

/**
 * Validate VIN form by checking the validity of the VIN or license plate.
 * Updates the state for the VIN field with the corresponding validation result.
 * Toggles the vinOpen state if the VIN is valid.
 */
export const validateVin = () => {
    // console.log("validateVin runs")

    const [isVinValid, vinError] = vinIsValid(vin);

    setVin((prevState) => {
        return {
            ...prevState,
            isValid: isVinValid,
            error: vinError
        }
    })

    if (isVinValid) {
        setVinOpen(!vinOpen)
    }
}

/**
 * Handle dealership selection by updating the dealership state.
 * @param {number} index - Index of the selected dealership.
 */
const dealershipClickHandler = (index) => {
    setDealershipDetails(false)
    setDealershipDetailsOpen(true)

    setDealership((prevState) => {
        return {
            ...prevState,
            value: index !== prevState.value ? index : null,
            isValid: index !== prevState.value ? prevState.isValid : false,
        }
    })
}

/**
 * Validates if a dealership is selected.
 * @returns {Array} - Array containing a boolean indicating if a dealership is selected, and an error message if it isn't.
 */
const isDealershipSelected = () => {
    let isValid;
    let error;

    if (dealership.value !== null && dealership.value !== "") {
        // console.log("is valid")
        isValid = true;
        error = null;
    } else {
        // console.log("is invalid")
        isValid = false;
        error = "Debés seleccionar un concesionario"
    }

    return [isValid, error];
}

/**
 * Validate dealership selection form by checking if a dealership is selected.
 * Updates the state for the dealership field with the corresponding validation result.
 * Toggles the dealershipOpen state if a dealership is selected.
 */
export const validateDealership = () => {
    const [isDealerSelected, dealershipError] = isDealershipSelected();

    setDealership((prevState) => {
        return {
            ...prevState,
            isValid: isDealerSelected,
            error: dealershipError
        }
    })

    if (isDealerSelected) {
        setDealershipOpen(!dealershipOpen)
    }
}

/**
 * Validate dealership details form by checking if a dealership is selected.
 * Updates the state for the dealership details field with the corresponding validation result.
 * Toggles the dealershipDetailsOpen state if a dealership is selected.
 */
export const validateDealershipDetails = () => {
    const [isDealerSelected, dealershipError] = isDealershipSelected();

    if (isDealerSelected) {
        setDealershipDetails(true)
        setDealershipDetailsOpen(!dealershipDetailsOpen)
    }
}